// Category chip (RESTYLE §2): a kind or category name as a tinted label with its shape mark.
//   <CategoryChip c size? count? title? className?>{label?}</CategoryChip>
//       a static label (span): event-stream kind column, legend chips, lane-chart legend, "Observed so far".
//       label defaults to the category name; pass the kind ("net.dns") for stream rows. size: sm (18px rows) | md (20px)
//   <CategoryChip c pressed onClick count? {...button attrs}>{label?}</CategoryChip>
//       a filter button (aria-pressed, 28px): pressed = tinted; unpressed = no tint, faint text, hollow mark.
//       Callers read e.shiftKey in onClick to solo a category.
// The mark wears the chip's text step, so it clears 3:1 on the tint; text clears 4.5:1 in both themes.
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { CatMark, type Category } from "./Glyphs";
import { cx } from "./cx";

type Common = { c: Category; count?: ReactNode; size?: "sm" | "md"; className?: string; children?: ReactNode };
type StaticProps = Common & { pressed?: undefined; title?: string };
type FilterProps = Common & { pressed: boolean } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-pressed" | "children" | "className">;

export function CategoryChip(props: StaticProps | FilterProps) {
  const { c, count, size = "md", className, children } = props;
  const body = (pressed: boolean) => (
    <>
      <CatMark c={c} hollow={!pressed} />
      <span>{children ?? c}</span>
      {count !== undefined && count !== null && <span className="ct">{count}</span>}
    </>
  );
  if (props.pressed === undefined) {
    return (
      <span className={cx("cchip", c, size === "sm" && "cchip--sm", className)} title={props.title}>
        {body(true)}
      </span>
    );
  }
  const { pressed, type = "button", c: _c, count: _n, size: _s, className: _cl, children: _ch, ...rest } = props;
  return (
    <button type={type} className={cx("cchip", c, className)} aria-pressed={pressed} {...rest}>
      {body(pressed)}
    </button>
  );
}
